// src/components/ApiStatusTab.jsx
import React, { useState, useEffect } from 'react';

const ApiStatusTab = ({ status = {} }) => {
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Get API status with visual indicators
  const getStatusBadge = (apiStatus) => {
    switch(apiStatus) {
      case 'up':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            Online
          </span>
        );
      case 'down':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            Offline
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-gray-400" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            Unknown
          </span>
        );
    }
  };
  
  // Simulate checking the APIs
  const checkApis = async () => {
    setChecking(true);
    
    // In a real implementation, you would make actual API calls here
    // For now, let's simulate a delay and the results
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setLastChecked(new Date());
    setChecking(false);
  };
  
  // Format the last checked time
  const formatLastChecked = () => {
    if (!lastChecked) return 'Never';
    
    const now = new Date();
    const diffMinutes = Math.floor((now - lastChecked) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes === 1) return '1 minute ago';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    return lastChecked.toLocaleString();
  };
  
  // Toggle a section expansion
  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium text-gray-800">API Status</h2>
        <button
          onClick={checkApis}
          disabled={checking}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm ${
            checking ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {checking ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt mr-2"></i>
              Check API Status
            </>
          )}
        </button>
      </div>
      
      <div className="text-sm text-gray-500">
        Last checked: {formatLastChecked()}
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {/* Database API */}
          <li>
            <div className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-2">
                    <i className="fas fa-database text-green-600"></i>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      Local Database API
                    </div>
                    <div className="text-sm text-gray-500">
                      PostgreSQL with caching support
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {getStatusBadge(status.database)}
                  <button
                    onClick={() => toggleSection('database')}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <i className={`fas fa-chevron-${expandedSection === 'database' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>
              
              {expandedSection === 'database' && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Endpoint</h4>
                      <p className="mt-1 text-sm text-gray-900">http://localhost:5000/api/health</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Last Response</h4>
                      <p className="mt-1 text-sm text-gray-900">{status.database === 'up' ? '200 OK' : status.database === 'down' ? '500 Error' : 'Unknown'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Features</h4>
                      <ul className="mt-1 text-sm text-gray-900 list-disc list-inside">
                        <li>Full-text search</li>
                        <li>Time-based caching</li>
                        <li>Data deduplication</li>
                        <li>Cache expiry controls</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Endpoints</h4>
                      <ul className="mt-1 text-sm text-gray-900 list-disc list-inside">
                        <li>/api/health</li>
                        <li>/api/search</li>
                        <li>/api/search/fulltext</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </li>
          
          {/* MPR API */}
          <li>
            <div className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-2">
                    <i className="fas fa-money-bill-wave text-blue-600"></i>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      Medicine Price Registry API
                    </div>
                    <div className="text-sm text-gray-500">
                      Department of Health - South Africa
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {getStatusBadge(status.mpr)}
                  <button
                    onClick={() => toggleSection('mpr')}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <i className={`fas fa-chevron-${expandedSection === 'mpr' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>
              
              {expandedSection === 'mpr' && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Endpoint</h4>
                      <p className="mt-1 text-sm text-gray-900">https://medicineprices.org.za/api/v2/search-lite</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Data Provided</h4>
                      <ul className="mt-1 text-sm text-gray-900 list-disc list-inside">
                        <li>Medicine names and pricing</li>
                        <li>NAPPI codes</li>
                        <li>Dosage forms</li>
                        <li>Single Exit Prices (SEP)</li>
                        <li>Generic alternatives count</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Rate Limits</h4>
                      <p className="mt-1 text-sm text-gray-900">Unknown - Use caching to minimize requests</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                      <p className="mt-1 text-sm text-gray-900">External API - May experience downtime or changes</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <a
                      href="https://medicineprices.org.za/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Visit MPR Website <i className="fas fa-external-link-alt ml-1"></i>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </li>
          
          {/* SAHPRA API */}
          <li>
            <div className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-100 rounded-md p-2">
                    <i className="fas fa-clipboard-check text-purple-600"></i>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      SAHPRA API
                    </div>
                    <div className="text-sm text-gray-500">
                      South African Health Products Regulatory Authority
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {getStatusBadge(status.sahpra)}
                  <button
                    onClick={() => toggleSection('sahpra')}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <i className={`fas fa-chevron-${expandedSection === 'sahpra' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>
              
              {expandedSection === 'sahpra' && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Endpoint</h4>
                      <p className="mt-1 text-sm text-gray-900">https://medapps.sahpra.org.za:6006/Home/getData</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Data Provided</h4>
                      <ul className="mt-1 text-sm text-gray-900 list-disc list-inside">
                        <li>Registration status and numbers</li>
                        <li>Approval dates</li>
                        <li>Active ingredients</li>
                        <li>Manufacturer information</li>
                        <li>Scheduling information</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Rate Limits</h4>
                      <p className="mt-1 text-sm text-gray-900">Unknown - Use caching to minimize requests</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                      <p className="mt-1 text-sm text-gray-900">External API - May experience downtime or changes</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <a
                      href="https://www.sahpra.org.za/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:text-purple-800"
                    >
                      Visit SAHPRA Website <i className="fas fa-external-link-alt ml-1"></i>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </li>
        </ul>
      </div>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-exclamation-triangle text-yellow-400"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              External APIs may experience downtime or changes. The application will use cached data when available.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiStatusTab;